import React, {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Outlet } from 'react-router-dom';
import {
  Alert,
  AlertRule,
  DeviceAttributes,
  Group,
  UserConfig,
  GatewayDetails,
  DeviceSummaryAttribute,
  SummaryMeta,
  ParametersType,
  ParameterAttribute,
  UserDetails,
  ParticleData,
  UserConfigV2,
  ClientAttribute,
  DataRateDeviceWise,
  ClientThresholdSettingsAttribute,
  EventAttribute,
  DeviceType
} from '../constants/telosair-interfaces';
import {
  getAlertRules,
  getAllGroups,
  getParametersList,
  getUserData,
  getAllGatewaylist,
  getGroupSummary,
  getDeviceSummary,
  getClientusers,
  getDataSummary,
  getUserConfigV2,
  getAllDevicesV2,
  getDeviceDataRate,
  getAlertsWithSearch,
  getClientById,
  getClientThresholdSettings,
  getEvents,
  getDeviceSubscriptions,
  DeviceSubscription
} from '../services/dashboard-service';
import { useAuth } from './auth.context';
import { ALL_PARAMETERS } from '../constants/global-constants';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';

type DataContextProps = {
  loading: boolean;
  devices: DeviceAttributes[];
  userConfigData: UserConfig;
  userConfigDataV2: UserConfigV2 | null;
  groupedData: Group[];
  alertRules: AlertRule[];
  allAlerts: Alert[];
  totalDevices: number;
  events: EventAttribute[];
  totalGroups: number;
  totalGateways: number;
  parameters: ParameterAttribute[];
  /**
   * Every parameter configured for the client, including the ones switched off
   * in settings. Only the settings screen wants this - everywhere else uses
   * `parameters`, which hides the disabled ones.
   */
  allParameters: ParameterAttribute[];
  deviceDataSummary: { [key: string]: DeviceSummaryAttribute };
  groupDataSummary: { [key: string]: DeviceSummaryAttribute };
  dataSummary: ParticleData;
  /** Whether `dataSummary` is a stale last-known fallback, and from when. */
  dataSummaryMeta: SummaryMeta;
  currentUser: UserDetails | null;
  clientData: ClientAttribute | null;
  dataRatePerDevice: DataRateDeviceWise | null;
  clientUsers: UserDetails[];
  clientThresholdSettings: ClientThresholdSettingsAttribute | null;
  groupTypes: { [key: string]: DeviceType };
  /** Plan of every device of the organisation (plans are bought per device). */
  deviceSubscriptions: DeviceSubscription[];
  fetchAlertRules: () => Promise<void>;
  fetchAllDevices: () => Promise<void>;
  fetchDeviceSummary: (startDate: number, endDate: number) => Promise<void>;
  fetchGroupSummary: (startDate: number, endDate: number) => Promise<void>;
  fetchUserData: () => Promise<void>;
  fetchAllGroups: () => Promise<void>;
  fetchAllUserList: () => Promise<void>;
  fetchAlerts: () => Promise<void>;
  fetchEvents: () => Promise<void>;
  fetchClientThresholdSettings: () => Promise<void>;
  fetchSummary: (startDate: number, endDate: number) => Promise<void>;
  refresh: () => Promise<void>;
  fetchClientData: (id: string) => Promise<void>;
  setUserConfigData: (data: UserConfig) => void;
  setParameters: (data: ParameterAttribute[]) => void;
  /**
   * Re-reads the parameter list from the API. Saving the list can replace the
   * client's rows (a client still on the shared defaults gets its own copies),
   * so ids in memory go stale and have to be resynced.
   */
  fetchParameterList: () => Promise<void>;
  setUserConfigDataV2: (data: UserConfigV2) => void;
  setDataSummary: (data: ParticleData) => void;
  setGroupDataSummary: (data: { [key: string]: DeviceSummaryAttribute }) => void;
  updateAlertData: (alert: Alert) => void;
  setClientThresholdSettings: (data: ClientThresholdSettingsAttribute) => void;
};

const DataContext = createContext({} as DataContextProps);

/**
 * DataProvider component.
 * @returns - Returns ReactElement.
 */
export function DataProvider(): ReactElement {
  const { authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groupedData, setGroupedData] = useState<Group[]>([]);
  const [devices, setDevices] = useState<DeviceAttributes[]>([]);
  const [gateways, setGateways] = useState<GatewayDetails[]>([]);
  const [clientUsers, setClientUsers] = useState<UserDetails[]>([]);
  const [events, setEvents] = useState<EventAttribute[]>([]);
  const [currentUser, setCurrentUser] = useState<UserDetails | null>(null);
  const [clientData, setClientData] = useState<ClientAttribute | null>(null);
  const [userConfigData, setUserConfigData] = useState<UserConfig>({});
  const [userConfigDataV2, setUserConfigDataV2] = useState<UserConfigV2 | null>(null);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
  const [clientThresholdSettings, setClientThresholdSettings] =
    useState<ClientThresholdSettingsAttribute | null>(null);
  const [allParameters, setParameters] = useState<ParameterAttribute[]>([]);
  // Parameters the client has switched off in settings stay out of every chart,
  // card and dropdown; the settings screen reads `allParameters` so they can be
  // switched back on. `isEnabled` undefined means enabled (pre-flag rows).
  const parameters = useMemo(
    () => allParameters.filter((parameter) => parameter.isEnabled !== false),
    [allParameters]
  );
  const [deviceDataSummary, setDeviceDataSummary] = useState<{
    [key: string]: DeviceSummaryAttribute;
  }>({});
  const [dataSummary, setDataSummary] = useState<ParticleData>({} as ParticleData);
  const [dataSummaryMeta, setDataSummaryMeta] = useState<SummaryMeta>({
    stale: false,
    timeStamp: null
  });
  /**
   * Replace the client-wide summary with live values (real-time pushes). Live
   * data is by definition not a stale fallback, so the flag is cleared too.
   * @param data - Fresh parameter values.
   */
  const setLiveDataSummary = useCallback((data: ParticleData): void => {
    setDataSummary(data);
    setDataSummaryMeta({ stale: false, timeStamp: null });
  }, []);
  const [groupDataSummary, setGroupDataSummary] = useState<{
    [key: string]: DeviceSummaryAttribute;
  }>({});
  const [dataRatePerDevice, setdataRatePerDevice] = useState<DataRateDeviceWise | null>(null);
  const [groupTypes, setGroupTypes] = useState<{ [key: string]: DeviceType }>({});
  const [deviceSubscriptions, setDeviceSubscriptions] = useState<DeviceSubscription[]>([]);
  /**
   * Function to get data rate of the device.
   */
  const fetchDataRate = async (): Promise<void> => {
    try {
      const deviceWiseData: DataRateDeviceWise = {};
      const { data } = await getDeviceDataRate();
      data.forEach((item: { data_rate_in_percentage: number; device_serial_number: number }) => {
        const rate = item.data_rate_in_percentage;
        if (rate >= 50) {
          deviceWiseData[item.device_serial_number] = {
            state: 'working',
            value: rate.toString()
          };
        } else if (rate < 50 && rate > 0) {
          deviceWiseData[item.device_serial_number] = {
            state: 'moderate',
            value: rate.toString()
          };
        } else {
          deviceWiseData[item.device_serial_number] = {
            state: 'default',
            value: rate.toString()
          };
        }
      });
      setdataRatePerDevice(deviceWiseData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchAlertRulesData function.
   */
  const fetchAlertRules = async (): Promise<void> => {
    try {
      const response = await getAlertRules();
      setAlertRules([...response.data]);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchEvents function.
   */
  const fetchEvents = async (): Promise<void> => {
    try {
      const response = await getEvents('EVENTS');
      setEvents([...response.data]);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchAllDevices function.
   */
  const fetchAllDevices = async (): Promise<void> => {
    try {
      const response = await getAllDevicesV2();
      if (response.data) {
        setDevices(response.data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchDeviceSummary function.
   * @param startDate - Start date.
   * @param endDate - End date.
   */
  const fetchDeviceSummary = async (startDate: number, endDate: number): Promise<void> => {
    try {
      const deviceDataResponse = await getDeviceSummary({
        startDate,
        endDate
      });
      const summary: { [key: string]: DeviceSummaryAttribute } = deviceDataResponse.data;
      if (Object.keys(summary).length > 0) {
        setDeviceDataSummary(summary);
      } else {
        setDeviceDataSummary({});
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchGroupSummary function.
   * @param startDate - Start date.
   * @param endDate - End date.
   */
  const fetchGroupSummary = async (startDate: number, endDate: number): Promise<void> => {
    try {
      const groupDataResponse = await getGroupSummary({
        startDate,
        endDate
      });
      const summary: { [key: string]: DeviceSummaryAttribute } = groupDataResponse.data;
      if (Object.keys(summary).length > 0) {
        setGroupDataSummary(summary);
      } else {
        setGroupDataSummary({});
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Function to get data summary from API.
   * @param startDate - Start date.
   * @param endDate - End date.
   */
  const fetchDataSummary = async (startDate: number, endDate: number): Promise<void> => {
    try {
      const dataResponse = await getDataSummary({
        startDate,
        endDate
      });
      if (dataResponse.data.parameters) {
        setDataSummary(dataResponse.data.parameters);
      } else {
        setDataSummary({} as ParticleData);
      }
      setDataSummaryMeta({
        stale: dataResponse.data.stale === true,
        timeStamp: dataResponse.data.stale === true ? dataResponse.data.timeStamp ?? null : null
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchUserData function.
   */
  const fetchUserData = async (): Promise<void> => {
    try {
      const response = await getUserData();
      setUserConfigData(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Fetches user config using V2 API.
   */
  const fetchUserConfigV2 = async (): Promise<void> => {
    try {
      const response = await getUserConfigV2();
      const parsedConfig = JSON.parse(response.data?.config || '{}');

      // Check if default config exists (for public view) and merge it
      const defaultConfigStr = sessionStorage.getItem('defaultUserConfig');
      if (defaultConfigStr) {
        const defaultConfig = JSON.parse(defaultConfigStr);
        // Merge default config with API config, prioritizing API values
        const mergedConfig = { ...defaultConfig, ...parsedConfig };
        setUserConfigDataV2({ ...response.data, config: mergedConfig });
      } else {
        setUserConfigDataV2({ ...response.data, config: parsedConfig });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls fetchAllGroups function.
   */
  const fetchAllGroups = async (): Promise<void> => {
    try {
      const response = await getAllGroups();
      setGroupedData([...response.data]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };
  /**
   * Calls All Gatewayslist.
   */
  const fetchAllGatewaylist = async (): Promise<void> => {
    try {
      const response = await getAllGatewaylist();
      setGateways(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Calls getParametersList function.
   */
  const fetchParameterList = async (): Promise<void> => {
    try {
      const response = await getParametersList();
      const list: ParameterAttribute[] = [];
      response?.data.forEach(
        (item: { parameterName: ParametersType; displayName: string; id: number }) => {
          const parameter = ALL_PARAMETERS[item.parameterName];
          list.push({
            ...parameter,
            unit: (parameter && userConfigData[parameter.unitKey as keyof UserConfig]) || '',
            ...item
          });
        }
      );
      const sortedList = list.sort((a, b) => a.order - b.order);
      setParameters(sortedList);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };
  /**
   * Calls All Gatewayslist.
   */
  const fetchAllUserList = async (): Promise<void> => {
    try {
      const response = await getClientusers();
      setClientUsers(response.data);
      const username = getUserNameFromSessionStorage();
      if (response.data.length > 0) {
        response.data.forEach((item: UserDetails) => {
          if (item.userName === username) {
            setCurrentUser(item);
          }
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Call get client by id API.
   * @param id - ID.
   */
  const fetchClientData = async (id: string): Promise<void> => {
    try {
      const response = await getClientById(id);
      setClientData(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Call getClientThresholdSettings.
   */
  const fetchClientThresholdSettings = async (): Promise<void> => {
    try {
      const response = await getClientThresholdSettings();
      setClientThresholdSettings(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Fetches the plan of every device for the current user's organisation.
   */
  const fetchDeviceSubscriptions = async (): Promise<void> => {
    try {
      const response = await getDeviceSubscriptions();
      setDeviceSubscriptions(response.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setDeviceSubscriptions([]);
    }
  };

  /**
   * FetchData for all the dashboard apis.
   * @returns - Promise.
   */
  const fetchData = async (): Promise<void> => {
    try {
      await Promise.all([
        fetchAllDevices(),
        fetchUserData(),
        fetchAllGroups(),
        fetchAlertRules(),
        fetchAllGatewaylist(),
        fetchAllUserList(),
        fetchParameterList(),
        fetchUserConfigV2(),
        fetchDataRate(),
        fetchClientThresholdSettings(),
        fetchEvents(),
        fetchDeviceSubscriptions()
      ]);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setLoading(false);
    }
  };
  /**
   * FetchData for group summary, device summary and data summary..
   * @param startDate - Start date.
   * @param endDate - End date.
   * @returns - Promise.
   */
  const fetchSummary = async (startDate: number, endDate: number): Promise<void> => {
    try {
      await Promise.all([
        fetchDeviceSummary(startDate, endDate),
        fetchGroupSummary(startDate, endDate),
        fetchDataSummary(startDate, endDate)
      ]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Function to fetch alert for each rule.
   */
  const fetchAlerts = async (): Promise<void> => {
    try {
      const activitiesResponse: any = await getAlertsWithSearch({
        searchString: '*',
        devices: [],
        parameters: []
      });
      const { data }: { data: Alert[] } = activitiesResponse;
      setAllAlerts([
        ...data.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      ]);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Updates alert data.
   * @param alert - Alert to be updated.
   */
  const updateAlertData = (alert: Alert): void => {
    setAllAlerts((prevItems) =>
      prevItems.map((item) => (item.id === alert.id ? { ...item, ...alert } : item))
    );
  };

  useEffect(() => {
    if (authUser) {
      fetchData();
      fetchAlerts();
    }
  }, [authUser]);

  useEffect(() => {
    if (currentUser?.clientId) {
      fetchClientData(currentUser.clientId?.toString());
    }
  }, [currentUser]);

  useEffect(() => {
    if (groupedData.length > 0 && devices.length > 0) {
      const types: { [key: string]: DeviceType } = {};
      groupedData.forEach((group) => {
        const deviceTypes = group.sensorDetailsList
          .map((sensor) => {
            const device = devices.find(
              (dev) => dev.id === sensor.id || dev.serialNumber === sensor.serial_number
            );
            return device?.deviceType;
          })
          .filter(Boolean);

        const hasIndoor = deviceTypes.some((type) => type === DeviceType.INDOOR);
        const hasOutdoor = deviceTypes.some((type) => type === DeviceType.OUTDOOR);

        let groupType: DeviceType;
        if (hasIndoor && hasOutdoor) {
          groupType = DeviceType.MIXED;
        } else if (hasIndoor) {
          groupType = DeviceType.INDOOR;
        } else {
          groupType = DeviceType.OUTDOOR;
        }

        types[group.groupId.toString()] = groupType;
      });
      setGroupTypes(types);
    }
  }, [groupedData, devices]);

  /**
   * Calls fetchData function.
   */
  const refresh = async (): Promise<void> => {
    await fetchData();
  };
  const DataProviderValue = useMemo(
    () => ({
      devices,
      loading,
      userConfigData,
      groupedData,
      alertRules,
      allAlerts,
      deviceDataSummary,
      totalDevices: devices.length,
      totalGroups: groupedData.length,
      totalGateways: gateways.length,
      clientUsers,
      groupDataSummary,
      parameters,
      allParameters,
      dataSummary,
      dataSummaryMeta,
      userConfigDataV2,
      clientData,
      dataRatePerDevice,
      currentUser,
      clientThresholdSettings,
      groupTypes,
      events,
      fetchEvents,
      setParameters,
      fetchParameterList,
      fetchAlertRules,
      fetchAllDevices,
      fetchDeviceSummary,
      fetchGroupSummary,
      fetchSummary,
      fetchAllGroups,
      fetchUserData,
      fetchAlerts,
      refresh,
      setUserConfigData,
      setUserConfigDataV2,
      setDataSummary: setLiveDataSummary,
      setGroupDataSummary,
      fetchClientData,
      fetchAllUserList,
      updateAlertData,
      fetchClientThresholdSettings,
      setClientThresholdSettings,
      deviceSubscriptions,
      fetchDeviceSubscriptions
    }),
    [
      devices,
      loading,
      groupedData,
      userConfigData,
      alertRules,
      allAlerts,
      devices.length,
      groupedData.length,
      gateways.length,
      clientUsers,
      deviceDataSummary,
      groupDataSummary,
      parameters,
      allParameters,
      dataSummary,
      dataSummaryMeta,
      userConfigDataV2,
      dataRatePerDevice,
      clientData,
      currentUser,
      clientThresholdSettings,
      groupTypes,
      events,
      fetchEvents,
      setParameters,
      fetchParameterList,
      fetchAlertRules,
      fetchAllDevices,
      fetchDeviceSummary,
      fetchSummary,
      fetchGroupSummary,
      fetchAllGroups,
      fetchUserData,
      fetchAlerts,
      refresh,
      setUserConfigData,
      setUserConfigDataV2,
      setDataSummary,
      setGroupDataSummary,
      fetchClientData,
      fetchAllUserList,
      updateAlertData,
      fetchClientThresholdSettings,
      setClientThresholdSettings,
      deviceSubscriptions,
      fetchDeviceSubscriptions
    ]
  );

  // if (loading) {
  //   return <Loader />;
  // }

  return (
    <DataContext.Provider value={DataProviderValue}>
      <Outlet />
    </DataContext.Provider>
  );
}

/**
 * Custom hook to use the DataContext and access alerts, devices, chartData, overviewChartData, loading, handleTimeChange, refresh state.
 * @returns - DataContext state.
 */
export const useData = (): DataContextProps => useContext(DataContext);
